import { Link } from 'react-router-dom'
import { PageLayout } from '@/shared/components/PageLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { PAGE_URLS, SERVICE_URLS } from '@/lib/constants'
import { content } from '@/lib/content'
import {
  Calendar,
  MapPin,
  Phone,
  Users,
  PartyPopper,
  Utensils,
  Heart,
  Building2,
  Gift,
  HandHeart,
  ArrowRight,
  Clock,
  Wine,
  ChevronDown,
  Star
} from 'lucide-react'

const serviceIcons = {
  'Wedding Receptions': Heart,
  'Corporate Functions': Building2,
  'Happier Holidays': Gift,
  'Birthday Parties': PartyPopper,
  'Funeral Services and Catering for Sensitive Occasions': HandHeart,
  'Other': Users,
}

const eventIcons = {
  'Queen of Hearts': Heart,
  'KC raffle': Gift,
  'Fish Fry': Utensils,
}

const services = content.home.services.items.map((item, index) => ({
  title: item.title,
  body: item.body,
  imageUrl: item.image,
  link: [
    SERVICE_URLS.WEDDING_RECEPTIONS,
    SERVICE_URLS.CORPORATE_FUNCTIONS,
    SERVICE_URLS.HAPPIER_HOLIDAYS,
    SERVICE_URLS.BIRTHDAY_PARTIES,
    SERVICE_URLS.FUNERAL_SERVICES,
    SERVICE_URLS.FUNDRAISERS,
  ][index],
  Icon: serviceIcons[item.title] || Users,
}))

const stats = [
  { value: '350+', label: 'Guest Capacity', icon: Users },
  { value: '2', label: 'Event Halls', icon: Building2 },
  { value: '7', label: 'Days a Week', icon: Calendar },
]

export default function HomePage() {
  const scrollToContent = () => {
    document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <PageLayout
      title={content.home.seo.title}
      description={content.home.seo.description}
    >
      {/* Hero Section - Immersive Full Screen */}
      <section className="relative min-h-screen flex items-center -mt-20">
        {/* Background Image with Warm Overlay */}
        <div className="absolute inset-0">
          <img
            src={content.home.hero.image}
            alt="KC Hall Event Venue"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/80 to-background/70" />
          <div className="absolute inset-0 pattern-bg" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 content-container pt-20">
          <div className="max-w-3xl">
            <Badge
              variant="outline"
              className="mb-6 border-primary/50 bg-primary/10 text-primary px-4 py-1.5"
            >
              <MapPin className="mr-2 h-3.5 w-3.5" />
              O'Fallon, Illinois
            </Badge>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-foreground mb-6 leading-[1.05]">
              {content.home.hero.title}
            </h1>

            <p className="text-xl md:text-2xl text-muted-foreground mb-4 max-w-xl">
              {content.home.hero.subtitle}
            </p>

            <p className="text-lg text-primary font-medium mb-10">
              {content.home.hero.location}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <Link to={PAGE_URLS.CONTACT}>
                <Button
                  size="lg"
                  className="h-14 px-8 text-base bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  {content.home.hero.buttons.contact}
                </Button>
              </Link>
              <Link to={PAGE_URLS.ACTIVITIES}>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-base border-border hover:bg-secondary cursor-pointer"
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  {content.home.hero.buttons.viewEvents}
                </Button>
              </Link>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <stat.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <button
          onClick={scrollToContent}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground hover:text-primary transition-colors cursor-pointer"
          aria-label="Scroll to content"
        >
          <ChevronDown className="h-8 w-8 animate-bounce" />
        </button>
      </section>

      {/* About Section */}
      <section id="about-section" className="section-padding">
        <div className="content-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Images Grid */}
            <div className="relative">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-7 row-span-2">
                  <img
                    src={content.home.whoWeAre.images[0].src}
                    alt={content.home.whoWeAre.images[0].alt}
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <div className="col-span-5">
                  <img
                    src={content.home.whoWeAre.images[1].src}
                    alt={content.home.whoWeAre.images[1].alt}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
                <div className="col-span-5">
                  <img
                    src={content.home.whoWeAre.images[2].src}
                    alt={content.home.whoWeAre.images[2].alt}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              </div>
              {/* Decorative Element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-primary/30 rounded-lg -z-10" />
            </div>

            {/* Text Content */}
            <div>
              <Badge variant="outline" className="mb-4 border-primary/50 text-primary">
                Welcome
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                {content.home.whoWeAre.heading}
              </h2>
              <Separator className="w-16 bg-primary mb-8" />
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {content.home.whoWeAre.description}
              </p>

              {/* Feature List */}
              <div className="space-y-4 mb-8">
                {[
                  { icon: Wine, text: 'Full-service bar open to the public' },
                  { icon: Utensils, text: 'Award-winning Friday fish fry' },
                  { icon: Users, text: 'Perfect for events of all sizes' },
                ].map((feature) => (
                  <div key={feature.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-foreground">{feature.text}</span>
                  </div>
                ))}
              </div>

              <Link to={PAGE_URLS.FACILITIES}>
                <Button variant="outline" className="cursor-pointer">
                  Explore Our Facilities
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Weekly Events Section */}
      <section className="section-padding bg-secondary/30">
        <div className="content-container">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4 border-primary/50 text-primary">
              Join Us
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {content.home.weeklyEvents.heading}
            </h2>
            <Separator className="w-16 bg-primary mx-auto mb-6" />
            <p className="text-lg text-muted-foreground">
              {content.home.weeklyEvents.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {content.home.weeklyEvents.events.map((event, index) => {
              const IconComponent = eventIcons[event.name] || Calendar
              return (
                <Card
                  key={index}
                  className="bg-card border-border hover:border-primary/50 transition-all duration-300 card-hover"
                >
                  <CardHeader className="text-center pb-2">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <IconComponent className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{event.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4 text-primary" />
                      <span className="text-sm">{event.description}</span>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>

          <div className="text-center">
            <Link to={PAGE_URLS.ACTIVITIES}>
              <Button size="lg" className="h-12 px-8 cursor-pointer">
                {content.home.weeklyEvents.button}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="content-container">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4 border-primary/50 text-primary">
              Our Services
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {content.home.services.heading}
            </h2>
            <Separator className="w-16 bg-primary mx-auto mb-6" />
            <p className="text-lg text-muted-foreground">
              {content.home.services.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Card
                key={service.title}
                className="group overflow-hidden bg-card border-border hover:border-primary/50 transition-all duration-300 card-hover"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={service.imageUrl}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 mb-1">
                      <service.Icon className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-semibold text-foreground">{service.title}</h3>
                    </div>
                  </div>
                </div>
                <CardContent className="p-5">
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4 line-clamp-2">
                    {service.body}
                  </p>
                  <Link to={service.link}>
                    <Button
                      variant="ghost"
                      className="p-0 h-auto text-primary hover:text-primary/80 hover:bg-transparent cursor-pointer"
                    >
                      Learn More
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to={PAGE_URLS.SERVICES}>
              <Button size="lg" variant="outline" className="h-12 px-8 cursor-pointer">
                {content.home.services.button}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={content.home.hero.image}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background/95" />
          <div className="absolute inset-0 pattern-bg" />
        </div>

        <div className="relative content-container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-8 rounded-full bg-primary/20 flex items-center justify-center">
              <Star className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Ready to Plan Your Event?
            </h2>
            <p className="text-lg text-muted-foreground mb-10 max-w-xl mx-auto">
              Contact us today to discuss your event needs. Our experienced team is here to help make your celebration unforgettable.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to={PAGE_URLS.CONTACT}>
                <Button
                  size="lg"
                  className="h-14 px-10 bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Get in Touch
                </Button>
              </Link>
              <Link to={PAGE_URLS.GALLERY}>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-10 border-border hover:bg-secondary cursor-pointer"
                >
                  View Gallery
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  )
}
